import { useState, ChangeEvent, useEffect, useContext } from 'react';

import { TextField, FormControl } from '@material-ui/core';
import { gql, useLazyQuery } from '@apollo/client';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { CustomFieldExtensionSchema } from '@backstage/plugin-scaffolder-react';
import useDebounce from 'react-use/lib/useDebounce';
import { UserContext } from '../../contexts/UserContext';

/**
 * The schema for the {@link ActiveDirectoryGroupPicker} properties.
 */
export const ActiveDirectoryGroupPickerSchema: CustomFieldExtensionSchema = {
  returnValue: {
    type: 'string',
  },
};

const GroupsQuery = gql`
  query GetUserGroups($login: String!, $filter: GroupListFilter) {
    identity {
      user(login: $login) {
        memberOf(filter: $filter) {
          name
        }
      }
    }
  }
`;

/**
 * The input props that can be specified under `ui:options` for the
 * `ActiveDirectoryGroupPicker` field extension.
 *
 * @public
 */
export type ActiveDirectoryGroupState = FieldExtensionComponentProps<
  string | undefined
>;

/**
 * The underlying component that is rendered in the form for the `ActiveDirectoryPicker`
 * field extension.
 * @param props - The properties passed to the component.
 * @public
 */
export const ActiveDirectoryGroupPicker = (
  props: ActiveDirectoryGroupState,
) => {
  const { formData, onChange, rawErrors, idSchema, required } = props;

  const [groups, setGroups] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [grpName, setGrpName] = useState<string | undefined>(undefined);
  const { login } = useContext(UserContext);

  const [fetchGroups, { loading, error }] = useLazyQuery(GroupsQuery, {
    errorPolicy: 'all',
    variables: { login: login, filter: { name: grpName } },
  });

  useEffect(() => {
    if (login && grpName) {
      fetchGroups().then(groupResults => {
        if (groupResults?.data) {
          setGroups(
            groupResults.data?.identity?.user?.memberOf?.map(
              (group: { name: string }) => group?.name,
            ),
          );
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grpName]);

  useDebounce(
    () => {
      setGrpName(inputValue);
    },
    500,
    [inputValue],
  );

  // Event Handler for the text box and to set the value
  const onHandleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setInputValue(value);
  };

  const onSelect = (_: any, value: string | null) => {
    if (!value) {
      return;
    }
    onChange(value);
  };

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
    >
      {error ? (
        <TextField
          error
          label="Error"
          defaultValue="No Groups Available"
          helperText="Failed to fetch group details."
          variant="outlined"
        />
      ) : (
        <Autocomplete
          id={idSchema?.$id}
          value={formData}
          loading={loading}
          loadingText="Loading.."
          options={groups}
          getOptionLabel={option => option}
          onChange={onSelect}
          autoSelect
          renderInput={params => (
            <TextField
              {...params}
              label="Groups available"
              margin="normal"
              helperText="Groups associated with user"
              variant="outlined"
              required
              onChange={onHandleChange}
              InputProps={params.InputProps}
            />
          )}
        />
      )}
    </FormControl>
  );
};
